import qs from 'qs';
import { i18nClient, MMeasure, MPhone } from '@sailor/i18n-web';
import { unicodeRtl as wrapUnicodeRtl } from '@sailor/i18n-rtl';
import { getRegionConfigs } from '@m2h/common/utils/location';
import {
  getLanguage, getRegion, isRTL, normalizeTrimmedString,
} from '@m2h/common/utils';
import EventCenter from './EventCenter';

type PhoneRegion = Parameters<typeof MPhone.getCountryCallingCodeByRegion>[0];
type DistanceUnit = Parameters<typeof MMeasure.formatDistance>[2];

interface FormatDistanceByRegionProps {
  region?: unknown;
  usingCurrentRegion?: unknown;
  meters?: unknown;
  distanceUnit?: unknown;
  numberNotTranslate?: unknown;
}

const LOCALE_IDENTIFIER_DID_UPDATE_EVENT = 'com.sailor.localeIdentifier.didUpdate';
const REGION_CODE_PATTERN = /^[A-Z]{2}$/;
const COUNTRY_CALLING_CODE_PATTERN = /^\d{1,3}$/;
const DISTANCE_UNITS = new Set<number>([0, 1, 2, 3, 4]);
const ARABIC_DIGIT_PATTERN = /[٠-٩]/g;
let completedLocaleIdentifier: string | undefined;
let localeUpdateSequence = 0;

function normalizeArabicNumber(text: string): string {
  return text
    .replace(ARABIC_DIGIT_PATTERN, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(/\u066b/g, '.')
    .replace(/\u066c/g, ',');
}

function getLocaleDisplayName(locale: string): string {
  try {
    const { supportLocales } = getRegionConfigs() || {};
    if (!Array.isArray(supportLocales)) return locale;

    const localizedDisplayName = supportLocales
      .find((item) => item?.locale === locale)
      ?.localizedDisplayName;
    return typeof localizedDisplayName === 'string' && localizedDisplayName.trim().length > 0
      ? localizedDisplayName
      : locale;
  } catch {
    return locale;
  }
}

export const Intl = {
  allTextLocales() {
    try {
      const { supportRegions, supportLocales } = getRegionConfigs() || {};
      const region = getRegion();
      // @ts-ignore
      const supportLocaleKeysOrigin = supportRegions?.[region]?.supportLocales || [];
      let supportLocaleKeys = supportLocaleKeysOrigin;
      // TODO：仅香港自动注入
      if (region === 'HK' || region === 'AE') {
        supportLocaleKeys = [...new Set([...supportLocaleKeysOrigin, 'zh'])];
      }
      const curSupportLocales: any[] = [];
      supportLocaleKeys.forEach((item) => {
        const locale = supportLocales.find((info) => info.locale === item);
        locale && curSupportLocales.push(locale);
      });

      console.log('[Intl] curSupportLocales', curSupportLocales);
      return curSupportLocales;
    } catch (e) {
      console.log(e);
      return [
        { locale: 'en', localizedDisplayName: 'English' },
        { locale: 'zh-HK', localizedDisplayName: '中文（繁體）' },
        {
          locale: 'zh',
          localizedDisplayName: '中文（简体）',
        },
      ];
    }
  },
  currentLocaleText() {
    try {
      const { supportLocales } = getRegionConfigs() || {};
      const locale = getLanguage();
      return supportLocales.find((item) => item.locale === locale)?.localizedDisplayName || 'en';
    } catch (e) {
      return 'en';
    }
  },
  updateLocaleIdentifier(props, callback) {
    const locale = props?.locale;
    if (typeof locale !== 'string' || locale.length === 0) {
      return;
    }

    try {
      if (window.self !== window.top && window.top?.Mach?.requireModule) {
        const hostIntl = window.top.Mach.requireModule('Intl');
        if (hostIntl && hostIntl !== Intl && typeof hostIntl.updateLocaleIdentifier === 'function') {
          hostIntl.updateLocaleIdentifier(props, callback);
          return;
        }
      }
    } catch (e) {
      // 跨域 iframe 无法访问顶层 runtime 时，继续使用当前页面的 Web Intl 实现。
    }

    if (completedLocaleIdentifier === undefined) {
      completedLocaleIdentifier = getLanguage();
    }
    const currentUpdateSequence = ++localeUpdateSequence;
    i18nClient.changeLanguage(locale, (err, t) => {
      if (err) {
        console.log(err, '语言切换失败');
      }
      callback && callback({ success: true, errMsg: '' });
      if (currentUpdateSequence !== localeUpdateSequence) {
        return;
      }

      const needSendNotification = locale !== completedLocaleIdentifier;
      completedLocaleIdentifier = locale;
      try {
        if (needSendNotification) {
          EventCenter.emit(LOCALE_IDENTIFIER_DID_UPDATE_EVENT, { locale });
        }
      } finally {
        const queryString = window.location.search.substring(1); // 获取 ? 后的内容
        const params = qs.parse(queryString);
        params.locale = locale;
        history.replaceState(null, '', `?${qs.stringify(params)}`);
        window.location.reload(); // 刷新页面
      }
    });
  },
  getCurrentLocaleInfo() {
    const region = getRegion() || 'HK';
    const locale = getLanguage() || 'en';
    return {
      region,
      locale,
      displayName: getLocaleDisplayName(locale),
    };
  },
  unicodeLtr(str: string) {
    return str;
  },
  unicodeRtl(str: string) {
    return wrapUnicodeRtl(str);
  },
  DateTimeFormat(str: string) {},
  isRTL,
  formatDistanceByRegion(props?: FormatDistanceByRegionProps): string | undefined {
    const {
      region,
      usingCurrentRegion,
      meters,
      distanceUnit = 0,
      numberNotTranslate,
    } = props || {};
    if (typeof meters !== 'number' || !Number.isFinite(meters) || meters < 0) return undefined;

    try {
      let requestedRegion: string | undefined;
      if (typeof region === 'string' && region.trim().length > 0) {
        requestedRegion = region;
      }
      if (
        !requestedRegion
        && (usingCurrentRegion === true || usingCurrentRegion === 1)
      ) {
        requestedRegion = getRegion();
      }
      if (typeof requestedRegion !== 'string' || requestedRegion.trim().length === 0) {
        return undefined;
      }
      const normalizedRegion = requestedRegion.trim().toUpperCase();
      if (
        typeof distanceUnit !== 'number'
        || !Number.isInteger(distanceUnit)
        || !DISTANCE_UNITS.has(distanceUnit)
      ) {
        return undefined;
      }

      const locale = normalizeTrimmedString(getLanguage()) || undefined;
      const formattedDistance = MMeasure.formatDistance(
        normalizedRegion,
        meters,
        distanceUnit as DistanceUnit,
        locale,
      );
      if (typeof formattedDistance !== 'string') return undefined;

      const shouldKeepLatinNumber = (
        (numberNotTranslate === 1 || numberNotTranslate === true)
        && locale?.split(/[-_]/)[0] === 'ar'
      );
      return shouldKeepLatinNumber
        ? normalizeArabicNumber(formattedDistance)
        : formattedDistance;
    } catch {
      return undefined;
    }
  },
  getCountryCallingCodeByRegion(region?: string): string | undefined {
    if (typeof region !== 'string') return undefined;

    const normalizedRegion = region.trim().toUpperCase();
    if (!REGION_CODE_PATTERN.test(normalizedRegion)) return undefined;

    try {
      const callingCode = MPhone.getCountryCallingCodeByRegion(normalizedRegion as PhoneRegion);
      return typeof callingCode === 'string' && COUNTRY_CALLING_CODE_PATTERN.test(callingCode)
        ? callingCode
        : undefined;
    } catch {
      return undefined;
    }
  },
};

export default Intl;
